// Angular
import { inject, Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, tap } from 'rxjs';

// Local
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { IOtherFilterField, otherFilterFields } from '@feature/crl/clerk-review-filter/other-filter-fields.model';
import { IOtherFilterOperation } from '@feature/crl/clerk-review-filter/other-filter-operations.model';

/**
 * An enum containing all of the possible clerk review filter fields.
 */
export enum ClerkReviewFilterFieldEnum {
  Court = "Court",
  Queue = "Queue",
  Judge = "Judge",
  Document = "Document",
  Authorizer = "Authorizer",
  Authorizers_Firm = "Authorizer's Firm",
  Case_Name = "Case Name",
  Case_Number = "Case Number",
  Case_Type = "Case Type",
  Division = "Division",
  Document_Id = "Document Id",
  Document_Title = "Document Title",
  Document_Type = "Document Type",
  Sender = "Sender",
  Senders_Firm = "Sender's Firm",
  Transaction = "Transaction"
}

/**
 * An interface to define the object structure of a clerk review 
 * filter selection.
 */
export interface IClerkReviewFilterSelection {
  /**
   * The filter identifier.
   * - Needed to identify the filter when removing.
   */
  filterId: string;

  /**
   * The field being filtered. 
   */
  filterField: IOtherFilterField;

  /**
   * The filter operation being performed. 
   */
  filterOperation: IOtherFilterOperation;

  /**
   * The value to filter the field on.
   */
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClerkReviewFilterSelectionService {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly clerkReviewFilterPayloadService = inject(ClerkReviewFilterPayloadService);

  /**
   * The collection of IClerkReviewFilterSelection objects saved in a BehaviorSubject.
   */
  private clerkReviewFilterSelections$$ = new BehaviorSubject<IClerkReviewFilterSelection[]>([]);

  /**
   * The collection of IClerkReviewFilterSelection objects exposed as an Observable.
   */
  clerkReviewFilterSelections$ = this.clerkReviewFilterSelections$$.asObservable().pipe(
    tap((selections: IClerkReviewFilterSelection[]) => {
      // A natural side-effect of adding/removing selections is to update the payload...

      // Remove all "Other Filter" field properties from the payload.
      const otherPropertyNames = Object.values(otherFilterFields) as IOtherFilterField[];
      otherPropertyNames.forEach((field: IOtherFilterField) => { 
        this.clerkReviewFilterPayloadService.removeOtherFilter(field) 
      });
    
      // Add selected "Other Filter" field properties to the payload.
      selections.forEach((selection: IClerkReviewFilterSelection) => {
        this.clerkReviewFilterPayloadService.setOtherFilter(
          selection.filterField,
          selection.filterOperation,
          selection.value
        );
      });
    })
  );

  /**
   * A public method to allow a filter to be stored in the collection of 
   * IClerkReviewFilterSelection objects.
   */
  addFilterSelection(filterField: IOtherFilterField, filterOperation: IOtherFilterOperation, value: string): void {
    // Set the filterId; the array index is always unique and sufficient here.
    const filterId = filterField.caption;

    // Create the new IClerkReviewFilterSelection object.
    const newFilterSelection: IClerkReviewFilterSelection = { 
      filterId, 
      filterField, 
      filterOperation, 
      value 
    };

    // The IClerkReviewFilterSelection objects with any pre-existing filter with matching filterId removed.
    const filteredClerkReviewFilterSelections: IClerkReviewFilterSelection[] = this.clerkReviewFilterSelections$$.value.filter((crfs: IClerkReviewFilterSelection) => {
      return crfs.filterId !== filterId;
    });

    // The filtered collection of IClerkReviewFilterSelection objects with the new object appended.
    const updatedClerkReviewFilterSelections: IClerkReviewFilterSelection[] = [
      ...filteredClerkReviewFilterSelections,
      newFilterSelection
    ];

    // Save the update collection of IClerkReviewFilterSelection objects.
    this.clerkReviewFilterSelections$$.next(updatedClerkReviewFilterSelections);
  }

  /**
   * A public method to allow a filter to be removed from the previously
   * stored collection of IClerkReviewFilterSelection objects.
   */
  removeFilterSelection(filterId: string): void {
    // Create a new collection of IClerkReviewFilterSelection objects and append the new object.
    const updatedClerkReviewFilterSelections: IClerkReviewFilterSelection[] = this.clerkReviewFilterSelections$$.value.filter((crfs: IClerkReviewFilterSelection) => {
      return crfs.filterId !== filterId;
    });

    // Save the update collection of IClerkReviewFilterSelection objects.
    this.clerkReviewFilterSelections$$.next(updatedClerkReviewFilterSelections);
  }

  /**
   * A public method to allow for the removal of all previously stored 
   * collection of IClerkReviewFilterSelection objects. 
   */
  clearFilterSelections(): void {
    // Reset the stored collection back to an empty array.
    this.clerkReviewFilterSelections$$.next([]);
  }

}
